// Module-level store is the documented exception to "no module state": analytics
// is an inherently global side-channel, and trackEvent (also module-scoped) must
// read the current pageview's experiment props without threading them through
// every call site. The provider writes here on mount; trackEvent reads here.
//
// Persistence (`sq_exp_active` in localStorage) closes a timing race: the
// provider sets these in a useEffect, but events can fire BEFORE that effect
// commits — the tracker's first auto-pageview, a child mounted above the
// provider in effect order, or any goal fired during initial load. Without a
// synchronously-readable fallback those events shipped with NO exp_<key> prop,
// so a per-variant funnel had holes on exactly the early steps. The assignment
// is deterministic from the sq_vid cookie, so the value persisted by the prior
// page load is always the correct one for this visitor — reading it back makes
// the props available on the very first event of every subsequent load, with no
// dependence on React effect ordering. (Not PII — an experiment key→variant map;
// mirrors the existing unconditional sessionStorage enrollment flags.)
const STORE_KEY = "sq_exp_active";

let activeProps: Record<string, string> = {};

const persist = (props: Record<string, string>): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(props));
  } catch {
    // private mode / disabled storage — in-memory activeProps still works for
    // this page; only the cross-load early-event fallback is lost.
  }
};

const readPersisted = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    // Trust only string→string entries (defensive against a tampered/legacy blob).
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter((e): e is [string, string] => typeof e[1] === "string"),
    );
  } catch {
    return {};
  }
};

/** Replace the active props with `exp_<key>=<variant>` for each assignment, and
 *  persist them so early events on later loads can read them synchronously. */
export const setActiveExperimentProps = (
  assignments: Partial<Record<string, string>>,
): void => {
  activeProps = Object.fromEntries(
    Object.entries(assignments)
      .filter((entry): entry is [string, string] => entry[1] != null)
      .map(([key, variant]) => [`exp_${key}`, variant]),
  );
  persist(activeProps);
};

/**
 * The current visitor's experiment props, keyed `exp_<key>` → variant. Returns
 * the in-memory set once the provider has run; before that (early events on a
 * fresh load) it falls back to the persisted set from a prior load, so a goal
 * fired during initial load still carries the tag. Empty only on a visitor's
 * very first page view, before enrollment has ever been computed.
 */
export const getActiveExperimentProps = (): Record<string, string> =>
  Object.keys(activeProps).length > 0 ? activeProps : readPersisted();
